import { useEffect } from 'react';
import { FieldPath, UseFormReturn, useFieldArray } from 'react-hook-form';

import { Button } from 'components/Button';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { useFieldAttributes } from 'hooks/useLoadContext';
import { LoadTextInput } from 'pages/LoadView/LoadInformation/Components';
import { AdditionalReference, NormalizedLoad } from 'types/Load';
import { getFieldAttribute, initFieldAttributes } from 'types/LoadAttributes';

type StopKey = 'pickup' | 'consignee';

const emptyRef = (): AdditionalReference => ({
  qualifier: '',
  number: '',
  weight: 0,
  pieces: 0,
  shouldSendToDriver: false,
});

function StopAdditionalReferences({
  formMethods,
  stopKey,
  title,
}: {
  formMethods: UseFormReturn<NormalizedLoad>;
  stopKey: StopKey;
  title: string;
}) {
  const { control } = formMethods;
  const allFieldAttrs = useFieldAttributes();
  const refsFieldAttr =
    getFieldAttribute(allFieldAttrs, `${stopKey}.additionalReferences`) ??
    initFieldAttributes;
  const sectionReadOnly = refsFieldAttr.isReadOnly;
  const sectionNotSupported = refsFieldAttr.isNotSupported;

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: `${stopKey}.additionalReferences`,
  });

  // Ensure array exists so we can render deterministic inputs.
  // Intentionally only runs on mount; `fields` identity changes per RHF render.
  useEffect(() => {
    if (!sectionNotSupported && (!fields || fields.length === 0)) {
      replace([emptyRef(), emptyRef()]);
    }
  }, [fields, replace, sectionNotSupported]);

  if (sectionNotSupported) {
    return null;
  }

  return (
    <Flex direction='col' gap='sm' className='w-full'>
      <Flex direction='col' gap='xs'>
        <Typography variant='h5' weight='medium'>
          {title}
        </Typography>
      </Flex>

      <Grid cols='1' gap='sm' className='w-full m-0'>
        {fields.map((field, idx) => (
          <Grid key={field.id} cols='5' gap='sm' className='w-full m-0'>
            <div className='col-span-2'>
              <LoadTextInput
                name={
                  `${stopKey}.additionalReferences.${idx}.qualifier` as FieldPath<NormalizedLoad>
                }
                label='Qualifier'
                placeholder='e.g. Claim Number, Customer PO Number, File Number'
                readOnly={sectionReadOnly}
              />
            </div>

            <div className='col-span-2'>
              <LoadTextInput
                name={
                  `${stopKey}.additionalReferences.${idx}.number` as FieldPath<NormalizedLoad>
                }
                label='Number'
                placeholder='Value'
                readOnly={sectionReadOnly}
              />
            </div>

            {!sectionReadOnly && (
              <Flex direction='col' justify='end' className='col-span-1'>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  className='h-8 mt-6'
                  onClick={() => remove(idx)}
                  buttonNamePosthog={null}
                >
                  Remove
                </Button>
              </Flex>
            )}
          </Grid>
        ))}
      </Grid>

      {!sectionReadOnly && (
        <Flex justify='start'>
          <Button
            type='button'
            variant='secondary'
            size='sm'
            className='h-8'
            onClick={() => append(emptyRef())}
            buttonNamePosthog={null}
          >
            Add reference
          </Button>
        </Flex>
      )}
    </Flex>
  );
}

export default function AdditionalReferencesForm({
  formMethods,
}: {
  formMethods: UseFormReturn<NormalizedLoad>;
}) {
  const allFieldAttrs = useFieldAttributes();
  const pickupRefsAttr =
    getFieldAttribute(allFieldAttrs, 'pickup.additionalReferences') ??
    initFieldAttributes;
  const consigneeRefsAttr =
    getFieldAttribute(allFieldAttrs, 'consignee.additionalReferences') ??
    initFieldAttributes;
  const isReadOnly = pickupRefsAttr.isReadOnly && consigneeRefsAttr.isReadOnly;

  return (
    <Flex direction='col' gap='lg' className='w-full'>
      <StopAdditionalReferences
        formMethods={formMethods}
        stopKey='pickup'
        title='Pickup Additional References'
      />
      <StopAdditionalReferences
        formMethods={formMethods}
        stopKey='consignee'
        title='Consignee Additional References'
      />
      {isReadOnly && (
        <Typography variant='body-xs' className='text-neutral-500'>
          Stop additional references are currently display-only for TAI updates.
        </Typography>
      )}
    </Flex>
  );
}
